import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { loadCourseTemplate, updateCourseModuleParticipants } from '../../../store/courseService';
import { createActionPlan } from '../../../store/participantService';
import { selectUsersForNames } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import Button from '../../formElements/Button';
import TextAreaField from '../../formElements/TextAreaField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ParticipantAttendanceStatusView from '../modules/participantAttendance/ParticipantAttendanceStatusView';
import { SequentialNotice } from '../utils/SequentialNotice';

import app from '../../../app.module.css';
import card from '../../../commonStyles/cardContainer.module.css';
import dropdown from '../courseStyles/rowDropDown.module.css';

const _CHARACTER_LIMIT = 1000;

const CurrentCourseDetails = ({ row, onClose, isDisabled, isSelfEmployedCoach }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [information, setInformation] = useState('');
    const [courseModules, setCourseModules] = useState([]);
    const [actionPlanData, setActionPlanData] = useState([]);
    const [isActionsDisabled, setIsActionsDisabled] = useState(true);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector(selectUsersForNames);
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentTemplate = useSelector((state) => state.entities.courseService.currentTemplate);

    // USEEFFECTS
    useEffect(() => {
        if (!row || Object.keys(row)?.length === 0) return;
        dispatch(loadCourseTemplate(row.courseTemplateId));
        setCourseModules(row.modules);
        const userIds = [...new Set(row.modules.map((el) => el.userId))];
        const missingAdviserIds = userIds.filter((el) => !users.find((entry) => entry.id === el));
        missingAdviserIds.length > 0 && dispatch(loadUsersMinimalDetails(missingAdviserIds));
        let notes = row.modules[0].participants?.map((el) =>
            el.participantId === currentParticipant.id ? el.notes : ''
        );
        let participantNotes = notes.filter((el) => el !== '');
        setInformation(participantNotes[0] || '');

        const isCoachDisabled =
            isSelfEmployedCoach && !row.modules.some((el) => el.userId === loggedInUser?.id);
        setIsActionsDisabled(isDisabled || isCoachDisabled);
    }, [row, isDisabled, isSelfEmployedCoach, loggedInUser?.id]);

    // EVENT HANDLERS
    const onInformationChange = (e) => setInformation(e.target.value);

    const onModuleChange = (module, moduleActionPlan) => {
        const modules = courseModules.map((el) =>
            el.completionOrder > module.completionOrder &&
            el.date + 'T' + el.endTime > format(new Date(), "yyyy-MM-dd'T'HH:mm")
                ? { ...el, participants: module.participants }
                : el.id === module.id
                  ? module
                  : el
        );
        setCourseModules(modules);
        if (moduleActionPlan) setActionPlanData((prev) => [...prev, moduleActionPlan]);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const modules = courseModules.map((el) => {
            // Here is a good place to update modules with any notes
            const updatedParticipants = el.participants?.map((el) =>
                el.participantId === currentParticipant.id ? { ...el, notes: information } : el
            );
            // eslint-disable-next-line
            const { checked, ...rest } = el;
            return { ...rest, participants: updatedParticipants };
        });
        modules.forEach((el) => {
            const msg = `Module details for module ${el.title} have been updated`;
            const { id, participants } = el;
            const payload = { participants };
            dispatch(updateCourseModuleParticipants(id, payload, msg));
        });
        actionPlanData.length > 0 && actionPlanData.forEach((el) => dispatch(createActionPlan(el)));
    };

    return !currentTemplate || currentTemplate.length < 1 || attendanceDetails?.length < 1 ? (
        <LoadingSpinner content="No current course information found" />
    ) : (
        <div>
            <form onSubmit={onSubmit} data-testid="course_details_form_start">
                <div className={dropdown.rowDropdown}>
                    <h3 className={app.sectionHeading}>
                        {row.name} ({row.code})
                    </h3>
                    <p className={app.paragraphContent} data-testid="course_description">
                        {row.description}
                    </p>
                    <div className={card.cardContainer}>
                        <h3 className={app.sectionHeading}>Module Selection</h3>
                        <div hidden={!currentTemplate.modulesInSetOrder}>
                            <SequentialNotice />
                        </div>
                        <div className={card.cards}>
                            {courseModules.map((el) => (
                                <div key={el.id}>
                                    <ParticipantAttendanceStatusView
                                        module={el}
                                        attendanceDetails={attendanceDetails}
                                        currentParticipant={currentParticipant}
                                        information={information}
                                        onModuleChange={onModuleChange}
                                        isModuleCheckDisabled={isDisabled || isSelfEmployedCoach}
                                        isStatusUpdateDisabled={
                                            isDisabled ||
                                            (isSelfEmployedCoach && loggedInUser?.id !== el.userId)
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={dropdown.notesField}>
                        <TextAreaField
                            id="information"
                            placeholder={
                                'Please type additional information about your participant for the trainer here...'
                            }
                            maxLength={_CHARACTER_LIMIT}
                            value={information || ''}
                            count={`${information?.length || 0}/${_CHARACTER_LIMIT}`}
                            disabled={isActionsDisabled}
                            rows={2}
                            onChange={onInformationChange}
                        />
                    </div>
                    <div className={dropdown.submit}>
                        <Button
                            id="courseDetailsSubmitButton"
                            disabled={isActionsDisabled}
                            content="Update Participant Course Details"
                            icon={true}
                            iconType={<KeyboardArrowRightIcon />}
                        />
                    </div>
                    <div className={dropdown.exit} onClick={onClose}>
                        Close details
                        <CloseIcon />
                    </div>
                </div>
            </form>
        </div>
    );
};

CurrentCourseDetails.propTypes = {
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    isSelfEmployedCoach: PropTypes.bool
};

export default CurrentCourseDetails;
